import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

declare module '@tanstack/react-table' {
  interface TableMeta<TData> {
    updateData?: (contactId: string) => () => void;
    onRowsChange?: () => () => void;
    // contact_id -> oldest outstanding scheduled activity start_date, for the "Event" line.
    activitiesByContactId?: Record<string, string>;
  }
}
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useMemo, useState, useCallback, useEffect, useRef, memo } from "react";
import { columns } from "./ContactsTableColumns";
import { useContacts } from "@/hooks/useContacts";
import { useVisibleContactActivities } from "@/hooks/useVisibleContactActivities";
import { useVirtualScrollPosition } from "@/hooks/useVirtualScrollPosition";
import { ContactTableToolbar } from "./ContactTableToolbar";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { Loader2, AlertCircle, Users, Upload, UserPlus, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Contact, SortField } from "@/types/contact";
import type { UseBulkSelectionReturn } from "@/hooks/useBulkSelection";
import { Table as TanstackTable } from "@tanstack/react-table";

// Map column IDs to sortable database field names
const COLUMN_SORT_MAP: Record<string, string> = {
  name: 'first_name',
  phone: 'primary_phone',
  relationship: 'relationship',
  type: 'type',
  rank: 'rank',
  status: 'status',
  last_contacted: 'last_contacted',
  remarket: 'remarket',
};

function SortIcon({ field, currentSort }: { field: string; currentSort: SortField | null }) {
  if (!currentSort || currentSort.field !== field) {
    return <ArrowUpDown className="h-3 w-3 opacity-30" />;
  }
  if (currentSort.direction === 'ASC') {
    return <ArrowUp className="h-3 w-3" />;
  }
  return <ArrowDown className="h-3 w-3" />;
}

// Virtual scrolling configuration
const ROW_HEIGHT = 80; // Fixed row height in pixels for virtualization
const EVENT_ROW_HEIGHT = 112; // Taller rows that also show the "Event" line (oldest outstanding activity); margin vs. clipping
const OVERSCAN = 10; // Number of rows to render outside viewport

// Props type for VirtualTableRow
interface VirtualTableRowProps {
  row: ReturnType<ReturnType<typeof useReactTable<Contact>>['getRowModel']>['rows'][number];
  virtualRow: { index: number; start: number; size: number };
  totalColumnSize: number;
  rowSelection: Record<string, boolean>;
}

// Memoized virtual row component - uses div elements instead of tr/td
// for Safari compatibility (WebKit strips table semantics when display:flex
// or position:absolute is applied to table elements)
const VirtualTableRow = memo(function VirtualTableRow({
  row,
  virtualRow,
  totalColumnSize,
}: VirtualTableRowProps) {
  return (
    <div
      role="row"
      data-index={virtualRow.index}
      data-state={row.getIsSelected() ? "selected" : undefined}
      className="hover:bg-muted/50 transition-colors border-b absolute left-0 w-full flex items-center data-[state=selected]:bg-muted"
      style={{
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`,
        WebkitTransform: `translateY(${virtualRow.start}px)`,
        willChange: 'transform',
      }}
    >
      {row.getVisibleCells().map((cell) => (
        <div
          key={cell.id}
          role="cell"
          className="p-2 flex items-center min-w-0 h-full"
          style={{
            width: `${(cell.column.getSize() / totalColumnSize) * 100}%`,
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: re-render if selection state changed for this row
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.virtualRow.index === nextProps.virtualRow.index &&
    prevProps.virtualRow.start === nextProps.virtualRow.start &&
    // `size` changes (80 -> EVENT_ROW_HEIGHT) when a row's Event line data loads — must
    // re-render so the row grows and the Event line appears (not just on the next scroll).
    prevProps.virtualRow.size === nextProps.virtualRow.size &&
    prevProps.totalColumnSize === nextProps.totalColumnSize &&
    prevProps.rowSelection?.[prevProps.row.id] === nextProps.rowSelection?.[nextProps.row.id]
  );
});

interface ContactsTableProps {
  searchTerm?: string;
  filters?: any;
  onDataLoad?: (data: { totalCount: number; invalidateCache: () => void }) => void;
  onSelectionChange?: (data: {
    bulkSelection: UseBulkSelectionReturn;
    selectedContacts: Contact[];
    table: TanstackTable<Contact>;
  }) => void;
  onCreateContact?: () => void;
  onImportContacts?: () => void;
}

export function ContactsTable({ filters = {}, onDataLoad, onSelectionChange, onCreateContact, onImportContacts }: ContactsTableProps) {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [columnSort, setColumnSort] = useState<SortField | null>(null);

  // Memoize filters, merging column sort (overrides filter-panel sort when active)
  const currentFilters = useMemo(() => ({
    ...filters,
    sort: columnSort ? [columnSort] : (filters.sort || null),
  }), [filters, columnSort]);

  // Use contacts hook with memoized filters
  const {
    contacts,
    loading,
    totalCount,
    error,
    hasMore,
    loadMore,
    isLoadingMore,
    invalidateCache,
  } = useContacts(currentFilters);

  // Notify parent when data loads (lifts totalCount and invalidateCache up)
  useEffect(() => {
    if (!loading && onDataLoad) {
      onDataLoad({ totalCount: totalCount || 0, invalidateCache });
    }
  }, [loading, totalCount, invalidateCache, onDataLoad]);

  // Bulk selection hook for "Select All X Results" functionality
  const bulkSelection = useBulkSelection(currentFilters);

  // Track whether the last selection change came from the table UI
  // to prevent the sync effect from overwriting immediate updates
  const selectionChangeFromTableRef = useRef(false);

  // Sync TanStack Table row selection with bulk selection state
  // This effect handles external changes to bulkSelection (e.g., "Select All X Results" button)
  // but skips when the change originated from the table's checkbox interactions
  useEffect(() => {
    // Skip sync if the change came from handleRowSelectionChange
    if (selectionChangeFromTableRef.current) {
      selectionChangeFromTableRef.current = false;
      return;
    }

    if (bulkSelection.mode === 'all_matching') {
      // In all_matching mode, select all visible rows except excluded ones
      const newSelection: Record<string, boolean> = {};
      for (const contact of contacts) {
        newSelection[contact.id] = !bulkSelection.excludedIds.has(contact.id);
      }
      setRowSelection(newSelection);
    } else if (bulkSelection.mode === 'explicit') {
      // In explicit mode, only select the specific IDs
      const newSelection: Record<string, boolean> = {};
      for (const id of bulkSelection.selectedIds) {
        newSelection[id] = true;
      }
      setRowSelection(newSelection);
    } else {
      // No selection
      setRowSelection({});
    }
  }, [bulkSelection.mode, bulkSelection.selectedIds, bulkSelection.excludedIds, contacts]);

  // Reference for virtualized table container
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Persist and restore scroll position across navigations
  useVirtualScrollPosition(tableContainerRef);

  // Infinite scroll: load more when user scrolls near the bottom
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || !hasMore || isLoadingMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    // Load more when within 500px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 500) {
      loadMore();
    }
  }, [hasMore, isLoadingMore, loading, loadMore]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Handle retry
  const handleRetry = useCallback(() => {
    console.log('[ContactsTable] Forcing cache invalidation and data refresh');
    invalidateCache(); // Immediately invalidate the cache
  }, [invalidateCache]);

  // Column sort toggle: ASC -> DESC -> clear
  const handleColumnSort = useCallback((field: string) => {
    setColumnSort(prev => {
      if (!prev || prev.field !== field) {
        return { field, direction: 'ASC' };
      }
      if (prev.direction === 'ASC') {
        return { field, direction: 'DESC' };
      }
      return null;
    });
  }, []);

  // No client-side sort override — server handles sorting via sort_config
  const sortedContacts = contacts;

  // Oldest outstanding scheduled activity per visible contact, for the "Event" line.
  const visibleContactIds = useMemo(() => sortedContacts.map((c) => c.id), [sortedContacts]);
  const { data: activitiesByContactId } = useVisibleContactActivities(visibleContactIds);

  // Handle row selection changes - integrate with bulk selection
  const handleRowSelectionChange = useCallback((updater: React.SetStateAction<Record<string, boolean>>) => {
    const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;

    // Apply visual state immediately so checkboxes update without waiting for effect
    setRowSelection(newSelection);

    // Flag that this change came from the table UI, so the sync effect won't overwrite it
    selectionChangeFromTableRef.current = true;

    // Determine which IDs changed
    const oldSelectedIds = new Set(Object.keys(rowSelection).filter(id => rowSelection[id]));
    const newSelectedIds = new Set(Object.keys(newSelection).filter(id => newSelection[id]));

    // Calculate diff to detect bulk operations (header checkbox select-all/deselect-all)
    const addedIds = new Set([...newSelectedIds].filter(id => !oldSelectedIds.has(id)));
    const removedIds = new Set([...oldSelectedIds].filter(id => !newSelectedIds.has(id)));

    // Clear-all: all rows deselected at once
    if (newSelectedIds.size === 0 && oldSelectedIds.size > 0) {
      bulkSelection.clearSelection();
      return;
    }

    // Select-all: multiple rows added at once (header checkbox click)
    if (addedIds.size > 1) {
      bulkSelection.setExplicitSelection(newSelectedIds);
      return;
    }

    // Deselect-all from header: multiple rows removed at once
    if (removedIds.size > 1) {
      bulkSelection.clearSelection();
      return;
    }

    // Single row toggle
    if (addedIds.size === 1) {
      bulkSelection.toggleId([...addedIds][0]);
      return;
    }
    if (removedIds.size === 1) {
      bulkSelection.toggleId([...removedIds][0]);
      return;
    }
  }, [rowSelection, bulkSelection]);

  // Memoize table instance - removed getPaginationRowModel as virtualization handles rendering
  const table = useReactTable({
    data: sortedContacts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      rowSelection,
    },
    getRowId: (row) => row.id, // Use contact ID for stable row identity with virtualization
    meta: {
      activitiesByContactId,
      updateData: useCallback((contactId: string) => {
        console.log('[ContactsTable] Contact updated, refreshing data:', contactId);
        // Request an immediate fetch to get the newest data
        invalidateCache();
        return () => {}; // No cleanup needed
      }, [invalidateCache]),
      onRowsChange: useCallback(() => {
        console.log('[ContactsTable] Rows changed, refreshing data');
        // Request an immediate fetch
        invalidateCache();
        return () => {}; // No cleanup needed
      }, [invalidateCache]),
    },
  });

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows;
      onSelectionChange({
        bulkSelection,
        selectedContacts: selectedRows.map(row => row.original),
        table,
      });
    }
  }, [onSelectionChange, bulkSelection, bulkSelection.effectiveCount, bulkSelection.mode, rowSelection, table]);

  // Get all rows for virtualization
  const { rows } = table.getRowModel();

  // Setup virtualizer for efficient row rendering
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    // A row that shows the "Event" line is a fixed step taller; every other row is ROW_HEIGHT.
    estimateSize: (index) =>
      activitiesByContactId?.[rows[index]?.id] ? EVENT_ROW_HEIGHT : ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  // Recompute row sizes once the activity data for the visible page loads (rows that
  // gain an Event line grow from ROW_HEIGHT to EVENT_ROW_HEIGHT).
  useEffect(() => {
    rowVirtualizer.measure();
  }, [activitiesByContactId, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Memoize the count display to prevent unnecessary rerenders
  const countDisplay = useMemo(() => (
    <div className="text-sm text-muted-foreground">
      {totalCount.toLocaleString()} contact{totalCount !== 1 ? 's' : ''}
    </div>
  ), [totalCount]);

  // Error display
  if (error && !loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 space-y-4 text-center">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div>
          <h3 className="text-lg font-semibold">Error loading contacts</h3>
          <p className="text-muted-foreground mt-2 mb-4">{error.message}</p>
          <Button onClick={handleRetry}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4">
      <ContactTableToolbar
        table={table}
        bulkSelection={bulkSelection}
        totalCount={totalCount || 0}
        currentFilters={currentFilters}
      />

      <div className="flex-1 border rounded-lg overflow-hidden bg-background flex flex-col">
        {/* Sticky table header */}
        <div className="bg-muted/50 border-b" style={{ scrollbarGutter: 'stable' }}>
          <Table style={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-0">
                  {headerGroup.headers.map((header) => {
                    const sortField = COLUMN_SORT_MAP[header.column.id];
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "font-medium h-10 text-muted-foreground",
                          sortField && "cursor-pointer select-none hover:text-foreground"
                        )}
                        style={{ width: `${(header.getSize() / table.getTotalSize()) * 100}%` }}
                        onClick={() => {
                          if (sortField) handleColumnSort(sortField);
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {!header.isPlaceholder &&
                            flexRender(header.column.columnDef.header, header.getContext())}
                          {sortField && (
                            <SortIcon field={sortField} currentSort={columnSort} />
                          )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
          </Table>
        </div>

        {/* Virtualized scrollable table body */}
        <div
          ref={tableContainerRef}
          className="overflow-auto"
          style={{
            height: 'calc(100vh - 300px)',
            minHeight: '400px',
            scrollbarGutter: 'stable',
            WebkitOverflowScrolling: 'touch',
            contain: 'strict',
          }}
        >
          {loading ? (
            <div className="h-24 flex items-center justify-center text-muted-foreground">
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary/70 mb-2" />
                <p>Loading contacts...</p>
              </div>
            </div>
          ) : rows.length > 0 ? (
            <div
              role="rowgroup"
              style={{
                height: `${totalSize}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                return (
                  <VirtualTableRow
                    key={row.id}
                    row={row}
                    virtualRow={virtualRow}
                    totalColumnSize={table.getTotalSize()}
                    rowSelection={rowSelection}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
              {filters?.groups?.length > 0 || filters?.search ? (
                // Filters are applied but no results
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  <AlertCircle className="h-10 w-10 mb-3 opacity-50" />
                  <p className="font-medium">No contacts match your filters</p>
                  <p className="text-sm mt-1">Try adjusting your filter criteria</p>
                </div>
              ) : (
                // No filters, no contacts - user has no access
                <div className="flex flex-col items-center justify-center max-w-md mx-auto">
                  <Users className="h-12 w-12 mb-4 text-muted-foreground/50" />
                  <h3 className="font-semibold text-lg mb-2">No contacts available</h3>
                  <p className="text-muted-foreground text-sm mb-4 text-center">
                    You haven't created any contacts yet and no contacts have been shared with you.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 text-sm">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onCreateContact}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create a new contact
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onImportContacts}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Import contacts
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground/70 mt-4">
                    Or ask a team member to share contacts or groups with you
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between px-2 py-2">
        {countDisplay}
        {isLoadingMore && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading more...
          </div>
        )}
      </div>
    </div>
  );
}
